import React, { useEffect, useMemo, useState } from 'react';
import { api, type Finding, type RemovalRequest, formatDate, riskTone, statusTone } from '../lib/api';

type ProfileData = {
  first_name?: string;
  last_name?: string;
  current_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  phone_number?: string;
  additional_emails?: string;
  aliases?: string;
  previous_addresses?: string;
  consent_to_autopilot?: boolean | number;
};

type Run = { id: string; scan_type: string; status: string; summary?: string; created_at?: string; completed_at?: string };
type DiscoveryStatus = {
  connectors: Record<string, boolean>;
  metrics: { totalFindings: number; highRisk: number; pending: number; possible: number; requestReady: number; verifiedSources?: number };
  runs: Run[];
  findings: Finding[];
};
type AutoStatus = {
  ready: boolean;
  missing: string[];
  consent: boolean;
  email: { ready: boolean; provider: string; reason?: string; fromEmail?: string; testSender?: boolean };
  counts: { requests: number; brokers: number; exactSources?: number };
};

const steps = [
  ['Profile', 'Uses only the identity fields you save.'],
  ['Search', 'Runs free search fallback, broker probes, and local-import compatible queries.'],
  ['Verify', 'Checks whether the reachable page contains your exact fields.'],
  ['Contact', 'Finds privacy/opt-out/contact pages and emails.'],
  ['Draft', 'Creates drafts only after verified source evidence.']
];

function profileReady(profile: ProfileData) {
  return Boolean(profile.first_name && profile.last_name && profile.current_address && profile.city && profile.state && profile.postal_code && profile.phone_number);
}

function safeText(value?: string, fallback = 'Not found yet') {
  const text = String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return fallback;
  if (text.length > 320) return `${text.slice(0, 320)}...`;
  return text;
}

function isNoise(finding: Finding) {
  const blob = `${finding.status || ''} ${finding.title || ''} ${finding.data_found || ''}`;
  return /provider setup|required|connector setup|<!doctype|<html|<script|raw page|html error/i.test(blob);
}

function isVerified(finding: Finding) {
  return /verified source match/i.test(finding.status || '');
}

function isReview(finding: Finding) {
  return !isNoise(finding) && !isVerified(finding);
}

const Pipeline: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData>({});
  const [discovery, setDiscovery] = useState<DiscoveryStatus | null>(null);
  const [auto, setAuto] = useState<AutoStatus | null>(null);
  const [requests, setRequests] = useState<RemovalRequest[]>([]);
  const [busy, setBusy] = useState<'scan' | 'draft' | 'full' | ''>('');
  const [step, setStep] = useState(0);
  const [message, setMessage] = useState('');
  const [showReview, setShowReview] = useState(false);

  async function refresh() {
    const [p, s, a, r] = await Promise.all([
      api<ProfileData>('/api/profile').catch(() => ({})),
      api<DiscoveryStatus>('/api/discovery/status').catch(() => null),
      api<AutoStatus>('/api/autopilot/run').catch(() => null),
      api<RemovalRequest[]>('/api/requests').catch(() => [])
    ]);
    setProfile(p || {});
    setDiscovery(s);
    setAuto(a);
    setRequests(r || []);
  }

  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    if (!busy) return;
    const timer = window.setInterval(() => setStep((value) => Math.min(value + 1, steps.length - 1)), 900);
    return () => window.clearInterval(timer);
  }, [busy]);

  const ready = profileReady(profile);
  const findings = (discovery?.findings || []).filter((f) => !isNoise(f));
  const verified = findings.filter(isVerified);
  const reviewItems = findings.filter(isReview);
  const exactUrls = findings.filter((f) => /Exact source|Direct broker/i.test(f.source_type || '') || f.url);
  const contacts = findings.filter((f) => /contact|privacy|opt.?out|email/i.test(`${f.data_found || ''} ${f.recommended_action || ''}`));
  const verifiedDrafts = requests.filter((r) => r.source_url && !/proactive|known broker catalog/i.test(r.generated_message || '') && /waiting|approval|queued|draft|manual/i.test(r.status));
  const latestRun = discovery?.runs?.[0];
  const emailReady = Boolean(auto?.email?.ready);
  const localCommand = `cd ~/Downloads/personal-data-removal-simple-v9 && PDR_APP_URL=https://personal-data-removal.pages.dev PDR_EMAIL=suhaibabdeljaber@gmail.com PDR_PASSWORD=Password123! npm run local:free-search`;

  const visibleFindings = useMemo(() => {
    const base = showReview ? [...verified, ...reviewItems] : verified;
    return base.slice(0, 12);
  }, [showReview, verified, reviewItems]);

  async function scanOnly() {
    if (!ready) { setMessage('Save My Info first. The scanner needs your exact name, address, phone, city, state, and email.'); return; }
    setBusy('scan'); setStep(1); setMessage('Running free exact-source scan. HTML/raw errors will be hidden and summarized.');
    try {
      const result = await api<{ summary: string }>('/api/discovery/scan', { method: 'POST', body: JSON.stringify({ scanType: 'full' }) });
      setStep(3); setMessage(result.summary || 'Scan finished.');
      await refresh();
    } catch (err: any) {
      setMessage(err?.message || 'Scan failed.');
    } finally {
      setBusy('');
    }
  }

  async function draftOnly() {
    if (!ready) { setMessage('Save My Info first so drafts can include your exact removal details.'); return; }
    setBusy('draft'); setStep(4); setMessage('Drafting only from verified exact-source matches.');
    try {
      const result = await api<{ summary: string }>('/api/autopilot/run', { method: 'POST', body: JSON.stringify({ mode: 'approval-first', maxEmails: 10 }) });
      setMessage(result.summary || 'Drafting complete.');
      await refresh();
    } catch (err: any) {
      setMessage(err?.message || 'Drafting failed.');
    } finally {
      setBusy('');
    }
  }

  async function fullPass() {
    if (!ready) { setMessage('Save My Info first. Then run Scan + draft.'); return; }
    setBusy('full'); setStep(1); setMessage('Scanning free sources, verifying pages, finding contacts, then drafting only verified matches.');
    try {
      const scan = await api<{ summary: string }>('/api/discovery/scan', { method: 'POST', body: JSON.stringify({ scanType: 'full' }) });
      setStep(4);
      const draft = await api<{ summary: string }>('/api/autopilot/run', { method: 'POST', body: JSON.stringify({ mode: 'approval-first', maxEmails: 10 }) });
      setMessage(`${scan.summary || 'Scan complete.'} ${draft.summary || 'Drafting complete.'}`);
      await refresh();
    } catch (err: any) {
      setMessage(err?.message || 'Scan + draft failed.');
    } finally {
      setBusy('');
    }
  }

  async function copyLocalCommand() {
    await navigator.clipboard?.writeText(localCommand);
    setMessage('Copied the free local browser-search command. Run it in Terminal for broader no-paid-key indexing, then refresh this page.');
  }

  return (
    <div className="v9Page">
      <section className="v9Hero">
        <div className="v9HeroText">
          <span className="eyebrow">Free-first exact-source scanner</span>
          <h1>Find the page first. Draft only when there is evidence.</h1>
          <p>No paid search key is required. The app uses free search fallback, direct broker probes, and an optional local scraper command from your Mac. It will not create broker email drafts unless a source is verified.</p>
        </div>
        <div className="v9Actions">
          <button className="primaryButton" onClick={fullPass} disabled={!!busy || !ready}>{busy === 'full' ? 'Running...' : 'Scan + draft'}</button>
          <button className="secondaryButton" onClick={scanOnly} disabled={!!busy || !ready}>{busy === 'scan' ? 'Scanning...' : 'Scan only'}</button>
          <button className="ghostButton" onClick={draftOnly} disabled={!!busy || !ready}>{busy === 'draft' ? 'Drafting...' : 'Draft verified'}</button>
        </div>
      </section>

      {!ready && <div className="notice warn">Required first: open <strong>My Info</strong> and save your name, address, phone, city/state, email, and consent.</div>}
      {message && <div className={`notice ${/fail|error|blocked|required|missing|html/i.test(message) ? 'danger' : 'good'}`}>{safeText(message, 'Done.')}</div>}

      <section className="v9Stats">
        <article><span>Exact URLs</span><strong>{exactUrls.length}</strong><small>source links checked</small></article>
        <article><span>Verified matches</span><strong>{verified.length}</strong><small>pages containing your fields</small></article>
        <article><span>Contacts found</span><strong>{contacts.length}</strong><small>emails/forms discovered</small></article>
        <article><span>Ready drafts</span><strong>{verifiedDrafts.length}</strong><small>evidence-backed requests</small></article>
      </section>

      <section className="v9MainGrid">
        <article className="panelCard v9Results">
          <div className="sectionHeader tightHeader">
            <div>
              <span className="eyebrow">Source evidence</span>
              <h2>{showReview ? 'Verified + review queue' : 'Verified exact-source matches'}</h2>
            </div>
            <button className="secondaryButton smallButton" onClick={() => setShowReview((v) => !v)}>{showReview ? 'Show verified only' : 'Show review queue'}</button>
          </div>

          <div className="v9SourceList">
            {visibleFindings.length === 0 ? (
              <div className="emptyState">
                <strong>No verified source match yet.</strong>
                <p>Run Scan only first. If free Cloudflare fetching is blocked by search engines, use the local scraper command in the right panel for broader no-paid-key search.</p>
              </div>
            ) : visibleFindings.map((finding) => (
              <article className="v9SourceCard" key={finding.id}>
                <div className="v9SourceTop">
                  <div>
                    <strong>{safeText(finding.title || finding.source, 'Source result')}</strong>
                    <p>{safeText(finding.data_found)}</p>
                    {finding.url && <a href={finding.url} target="_blank" rel="noreferrer">Open exact source</a>}
                  </div>
                  <div className="rowTags">
                    <span className={`badge ${statusTone(finding.status)}`}>{finding.status}</span>
                    <span className={`badge ${riskTone(finding.risk_level)}`}>{finding.risk_level}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </article>

        <aside className="panelCard v9SidePanel">
          <span className="eyebrow">Scanner status</span>
          <h2>How this version searches</h2>
          <div className="v9Checklist">
            <div><strong>Free web fallback</strong><span>On</span></div>
            <div><strong>Direct broker probes</strong><span>On</span></div>
            <div><strong>Source verification</strong><span>On</span></div>
            <div><strong>Email sending</strong><span>{emailReady ? 'Configured' : 'Needs verified sender'}</span></div>
          </div>
          <div className="miniNote">
            <strong>No paid key path</strong>
            <p>For wider search without Brave/Google API costs, run the local scraper from your Mac. It imports exact URLs back into this dashboard.</p>
            <button className="secondaryButton smallButton full" onClick={copyLocalCommand}>Copy local scraper command</button>
          </div>
          <div className="miniNote">
            <strong>Last run</strong>
            <p>{latestRun?.summary ? safeText(latestRun.summary) : 'No scan run yet.'}</p>
            <small>{formatDate(latestRun?.completed_at || latestRun?.created_at)}</small>
          </div>
        </aside>
      </section>

      <section className="panelCard v9Process">
        <div className="sectionHeader tightHeader">
          <div><span className="eyebrow">Pipeline</span><h2>Live indexing steps</h2></div>
        </div>
        <div className="processLine">
          {steps.map(([title, detail], index) => (
            <article key={title} className={`processStep ${index <= step ? 'active' : ''} ${busy && index === step ? 'running' : ''}`}>
              <span>{index + 1}</span>
              <strong>{title}</strong>
              <p>{detail}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Pipeline;
